// ============================================================================
// CONFIG GROUP SIDEBAR
// ============================================================================
// Left navigation panel showing config groups

import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ConfigGroup } from 'api/types/config.types';

interface ConfigGroupSidebarProps {
  groups: ConfigGroup[];
  activeGroupKey: string | null;
  onGroupSelect: (groupKey: string) => void;
  loading?: boolean;
}

/** Section headings by config_group.setup_area (settings cleanup C7), in this order. */
const AREA_HEADINGS: Array<[string | null, string]> = [
  ['MODULES', 'Modules'],
  ['BOOKING', 'Booking'],
  ['FRONT_OFFICE', 'Front Office'],
  ['TAX_BILLING', 'Tax & Billing'],
  ['NOTIFICATIONS', 'Notifications'],
  ['APPROVALS', 'Approvals'],
  ['PROPERTY_TERMS', 'Property Terms'],
  [null, 'General'],
];

// Map DB icon names (kebab-case) to IconSystem keys (camelCase)
const ICON_MAP: Record<string, keyof typeof IconSystem> = {
  'currency-dollar': 'dollarSign',
  'x-circle': 'xCircle',
  'lock': 'lock',
  'users': 'users',
  'calendar-check': 'calendar',
  'clock': 'clock',
  'check-square': 'checkCircle',
  'fileText': 'file',
  'credit-card': 'creditCard',
  'percent': 'percent',
};

const ConfigGroupSidebar: React.FC<ConfigGroupSidebarProps> = ({
  groups,
  activeGroupKey,
  onGroupSelect,
  loading,
}) => {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, groupKey: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onGroupSelect(groupKey);
      }
    },
    [onGroupSelect]
  );

  if (loading) {
    return (
      <div className="config-group-sidebar">
        <div className="config-loading">Loading groups...</div>
      </div>
    );
  }

  const renderGroup = (group: ConfigGroup) => {
    const iconKey = ICON_MAP[group.icon ?? ''];
    const IconComponent = iconKey ? IconSystem[iconKey] : null;

    return (
      <div
        key={group.groupKey}
        role="button"
        tabIndex={0}
        className={`config-group-item${
          activeGroupKey === group.groupKey ? ' active' : ''
        }`}
        onClick={() => onGroupSelect(group.groupKey)}
        onKeyDown={(e) => handleKeyDown(e, group.groupKey)}
      >
        {IconComponent && (
          <span className="config-group-icon">
            <IconComponent size={18} />
          </span>
        )}
        <span>{group.displayName}</span>
      </div>
    );
  };

  // Headings only when the groups span more than one area; groups keep their display order.
  const sections = AREA_HEADINGS
    .map(([area, heading]) => ({ heading, items: groups.filter((g) => (g.setupArea ?? null) === area) }))
    .filter((s) => s.items.length > 0);
  const known = new Set(AREA_HEADINGS.map(([area]) => area));
  const unknown = groups.filter((g) => !known.has(g.setupArea ?? null));

  return (
    <div className="config-group-sidebar">
      {sections.length > 1
        ? sections.map((s) => (
            <React.Fragment key={s.heading}>
              <div className="config-group-heading">{s.heading}</div>
              {s.items.map(renderGroup)}
            </React.Fragment>
          ))
        : groups.map(renderGroup)}
      {sections.length > 1 && unknown.map(renderGroup)}
    </div>
  );
};

export default ConfigGroupSidebar;
